import { accessTokenStore } from "../api/access-token"
import { fetchContext, logout } from "../api/auth.api"
import { onSessionLost, onTenantAccessLost, restoreSession } from "../api/http"
import { isApiProblem, transportProblem } from "../api/problem"
import type { AccessState, AnonymousReason } from "./access-state"
import { wipeOfflineCache } from "../offline/register"
import { toPermissionSet } from "./permissions"

/**
 * Хранилище состояния доступа.
 *
 * ПОЧЕМУ НЕ ХУК И НЕ КОНТЕКСТ REACT. Профиля ждёт не только отрисовка, но и маршрутизатор:
 * проверка прав на маршруте происходит в beforeLoad, то есть вне дерева React. Если
 * состояние живёт только в контексте, маршруту приходится решать о правах раньше, чем
 * профиль загружен, — и человек на долю секунды видит «нет доступа». Поэтому загрузка
 * живёт здесь, её обещание доступно и маршрутизатору, и компонентам, а React подписывается
 * на неё через useSyncExternalStore.
 *
 * Побочная польза: двойной вызов эффектов в StrictMode не устраивает двойную ротацию
 * refresh-токена — загрузка идёт в одном экземпляре независимо от дерева.
 */

let state: AccessState = { kind: "checking" }
const listeners = new Set<() => void>()

/** Текущая загрузка. Гарантирует, что профиль запрашивается в одном экземпляре. */
let inFlight: Promise<AccessState> | null = null

function publish(next: AccessState): AccessState {
  state = next
  for (const listener of listeners) listener()
  return next
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getState(): AccessState {
  return state
}

/**
 * Отказ → состояние. Здесь и решается, что человек увидит, поэтому каждая ветка
 * подписана: перепутать «сорвалась сеть» с «нет доступа» — самая дорогая ошибка кабинета.
 */
function stateFromFailure(error: unknown): AccessState {
  const problem = isApiProblem(error) ? error : transportProblem("unknown")

  // Сессии нет: пароль спрашивать законно.
  if (problem.sessionLost) {
    return {
      kind: "anonymous",
      reason: problem.code === "session_reuse_detected" ? "reuse_detected" : "expired",
    }
  }

  // Салон не выбран, недоступен или заблокирован — это вопрос выбора салона,
  // а не входа: пароль человек уже ввёл, и требовать его снова незачем.
  if (
    problem.code === "tenant_not_selected" ||
    problem.code === "tenant_forbidden" ||
    problem.code === "tenant_suspended" ||
    problem.code === "tenant_access_revoked"
  ) {
    return { kind: "tenant_required", problem }
  }

  // Всё остальное — сбой, а не отказ. Сессия цела, права неизвестны: показываем сбой
  // и «Повторить», но НЕ экран входа и НЕ «нет доступа».
  return { kind: "failed", problem }
}

async function load(): Promise<AccessState> {
  publish({ kind: "checking" })
  try {
    // Токена нет — значит страницу обновили: сессию поднимает cookie с refresh.
    if (accessTokenStore.isEmpty()) await restoreSession()
    const context = await fetchContext()
    return publish({
      kind: "ready",
      context,
      permissions: toPermissionSet(context.permissions),
    })
  } catch (error) {
    return publish(stateFromFailure(error))
  }
}

function run(): Promise<AccessState> {
  if (inFlight !== null) return inFlight
  const started = load()
  inFlight = started.finally(() => {
    inFlight = null
  })
  return inFlight
}

/**
 * Дождаться профиля. Зовётся маршрутом перед тем, как судить о правах; повторные вызовы
 * бесплатны. Если состояние уже определено — возвращается оно, без нового запроса.
 */
function ensureLoaded(): Promise<AccessState> {
  if (inFlight !== null) return inFlight
  if (state.kind !== "checking") return Promise.resolve(state)
  return run()
}

/** Перечитать профиль: после входа, после выбора салона и по кнопке «Повторить». */
function reload(): Promise<AccessState> {
  if (inFlight !== null) return inFlight
  return run()
}

/**
 * Перечитать профиль ТИХО: без промежуточного «checking». Для правок, после которых
 * профиль меняется в мелочи (например, «пароль изменён сегодня»), а экран уже открыт:
 * reload() сбрасывал бы оболочку в каркас загрузки, и только что показанное «Пароль изменён»
 * исчезало бы вместе с размонтированным экраном.
 */
function refresh(): Promise<AccessState> {
  if (inFlight !== null) return inFlight
  if (state.kind !== "ready") return run()
  const started = (async (): Promise<AccessState> => {
    try {
      const context = await fetchContext()
      return publish({ kind: "ready", context, permissions: toPermissionSet(context.permissions) })
    } catch (error) {
      return publish(stateFromFailure(error))
    }
  })()
  inFlight = started.finally(() => {
    inFlight = null
  })
  return inFlight
}

function markAnonymous(reason: AnonymousReason): void {
  /*
   * КЕШ РАСПИСАНИЯ СТИРАЕТСЯ ВМЕСТЕ С СЕССИЕЙ, ЛЮБОЙ ПРИЧИНОЙ (П1-12).
   *
   * В нём лежат имена клиентов и их телефоны в пределах прав. Телефон мастера — самое
   * частое «чужое устройство» салона: его теряют, меняют, отдают детям. Поэтому стирание
   * привязано не к кнопке «Выйти», а к САМОМУ факту «человека больше нет»: сюда приходят
   * и выход, и оборванный refresh (признак кражи), и увольнение. Кнопка — лишь один
   * из трёх путей, и защищать надо все три.
   *
   * Ожидания здесь нет намеренно: экран входа обязан появиться сразу. Сам выход ждёт
   * стирания отдельно (см. signOut), а работник добьёт остатки на следующей активации.
   */
  void wipeOfflineCache()
  publish({ kind: "anonymous", reason })
}

async function signOut(): Promise<void> {
  try {
    await logout()
  } finally {
    // Здесь стирания ЖДЁМ: человек нажал «Выйти» на чужом устройстве и стоит над ним.
    // Вкладку закроют через секунду, и незаконченное стирание оставило бы кеш с именами
    // клиентов следующему, кто откроет кабинет без связи.
    await wipeOfflineCache()
    markAnonymous("signed_out")
  }
}

// Сессию могли оборвать в любой момент, не только на загрузке профиля: например,
// запрос журнала наткнулся на украденный refresh. Тогда весь кабинет обязан узнать
// об этом сразу, а не при следующем переходе.
onSessionLost((reason) => {
  markAnonymous(reason)
})

/*
 * Салон закрыли или доступ к нему отозвали посреди работы. Человек при этом НЕ выкидывается
 * на вход: он вошёл, и сессия жива — закрыт салон, а это другой разговор и другой экран.
 *
 * ДВА СЛУЧАЯ РАЗВЕДЕНЫ, ПОТОМУ ЧТО ЭТО РАЗНЫЕ НОВОСТИ.
 *
 * «Салон приостановлен» — профиль по-прежнему читается, и статус в нём новый: перечитываем
 * тихо, а экран ограничения соберёт оболочка. Сбрасывать всё в каркас загрузки незачем.
 *
 * «Вас убрали из салона» — профиля этого салона больше нет, и сессия уже отцеплена
 * от него сервером. Состояние выставляется СРАЗУ и с причиной: перечитывать профиль
 * бессмысленно (он ответит безликим «выберите салон»), а человеку надо сказать, из какого
 * именно салона его убрали, — иначе он звонит в оба.
 */
onTenantAccessLost((problem) => {
  if (problem.code === "tenant_access_revoked") {
    publish({ kind: "tenant_required", problem })
    return
  }
  void refresh()
})

export const accessStore = {
  getState,
  subscribe,
  ensureLoaded,
  reload,
  refresh,
  markAnonymous,
  signOut,
}

/** Тип хранилища — им типизируется контекст маршрутизатора. */
export type AccessStore = typeof accessStore
